/**
 * ObjectEditPanel React component
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2018 contexagon GmbH
 */


// react
import React from 'react';
import {connect} from 'react-redux';

// material UI
import {withStyles} from "@material-ui/core";

// material UI components
import Button from '@material-ui/core/Button'
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Badge from '@material-ui/core/Badge';
import Typography from '@material-ui/core/Typography';
import { Tooltip } from '@material-ui/core';

// icons
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import ChatIcon from '@material-ui/icons/Chat';
import SmsFailedIcon from '@material-ui/icons/SmsFailed';
import HistoryIcon from '@material-ui/icons/History';
import LinkIcon from '@material-ui/icons/Link';
import CodeIcon from '@material-ui/icons/Code';

// application
import ObjectHeader from './ObjectHeader';
import ObjectFieldSetPanel from "./ObjectFieldSetPanel";
import ObjectFieldSet from "./ObjectFieldSet";
import ObjectTabSet from "./ObjectTabSet";
import ObjectField from "./ObjectField";
import ObjectHistoryPanel from "../information/ObjectHistoryPanel";
import ObjectReferencePanel from "../information/ObjectReferencePanel";
import ObjectJsonPanel from "../information/ObjectJsonPanel";
import TagList from "../base/TagList";
import WarningPanel from "../base/WarningPanel";

import {deleteTag, addTag, changeTab, refreshObjectReferences} from '../../operations/editing';
import {updateObjectValueAction, updateWholeObjectAction} from '../../redux/editing/actions';
import {selectCurrentTabIndexes} from '../../redux/editing/selectors';
import {ACCESS_HIDDEN, ACCESS_WRITABLE, ACCESS_UNDEFINED} from "../../redux/authorization/consts";
import {toUserFriendlyDateTimeString} from "../../utils";


const OBJECT_ACCESS_TAB_INDEX = -1;
const OBJECT_REMARKS_TAB_INDEX = -2;
const OBJECT_REFERENCE_TAB_INDEX = -3;
const OBJECT_HISTORY_TAB_INDEX = -4;
const OBJECT_JSON_TAB_INDEX = -5;


/**
 * ObjectEditPanel is a React component for editing a single object
 *
 * @author Maurizio Tidei, Jens Gruschel
 */
export class ObjectEditPanel extends React.Component {

    handleDeleteTag = (index) => {
        this.props.deleteTag(this.props.objectId, index);
    };

    handleAddTag = (tag) => {
        this.props.addTag(this.props.objectId, tag);
    };

    handleUndoChange = (object) => {
        this.props.updateWholeObjectAction(this.props.objectId, object);
        this.props.refreshObjectReferences(object);
    };

    render() {

        if (!this.arePropsValid()) return null;

        const { classes } = this.props;

        const mainTabKey = "MAIN";
        const tabIndex = this.props.tabIndexes[mainTabKey] || 0;
        const accessRestricted = this.props.object.objectaccess &&
                                 this.props.object.objectaccess.accessibility &&
                                 this.props.object.objectaccess.accessibility !== "writable";

        return (
            <div>
                <ObjectHeader objectType={this.props.objectType} object={this.props.object} modified={this.props.modified}/>

                <Typography component="div">
                    <AppBar className={classes.tabs} position="static" color="default">
                        <Tabs
                            value={tabIndex}
                            onChange={(event, index) => {this.props.changeTab(mainTabKey, index)}}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {this.props.fieldSets.map(
                                fieldset => <Tab key={fieldset.id} label={fieldset.name}/>
                            )}
                            {this.props.hasRemarkTab && <Tab
                                key={"__remarks"}
                                icon={<Tooltip title="Bemerkungen">{
                                    this.props.object.todo ? <SmsFailedIcon/> : this.props.object.remarks ? <ChatIcon/> : <ChatBubbleOutlineIcon/>
                                }</Tooltip>}
                                value={OBJECT_REMARKS_TAB_INDEX}
                            />}
                            {this.props.changeObjectAccess && <Tab
                                key={"__objectaccess"}
                                icon={<Tooltip title="Zugriff">{
                                    accessRestricted ? <LockIcon/> : <LockOpenIcon/>
                                }</Tooltip>}
                                value={OBJECT_ACCESS_TAB_INDEX}
                            />}
                            {this.props.referencedBy.length > 0 && <Tab
                                key={"__objectreferences"}
                                icon={<Tooltip title="Verwendung">
                                    <Badge badgeContent={this.props.referencedBy.length} color="primary">
                                        <LinkIcon/>
                                    </Badge>
                                </Tooltip>}
                                value={OBJECT_REFERENCE_TAB_INDEX}
                            />}
                            {this.props.accessObjectHistory && <Tab
                                key={"__objecthistory"}
                                icon={<Tooltip title="Historie"><HistoryIcon/></Tooltip>}
                                value={OBJECT_HISTORY_TAB_INDEX}
                            />}
                            {this.props.accessObjectJson && <Tab
                                key={"__objectjson"}
                                icon={<Tooltip title="Code"><CodeIcon/></Tooltip>}
                                value={OBJECT_JSON_TAB_INDEX}
                            />}
                        </Tabs>
                    </AppBar>

                    <div className={classes.fieldsContainer}>
                        {this.renderFieldsOrTabs(tabIndex)}
                    </div>
                    
                    {
                        tabIndex === 0 && this.props.object.tags && <div style={{margin:"20px 80px 0px 75px", textAlign:"left"}}>
                            <TagList
                                tags={this.props.object.tags}
                                onDelete={this.handleDeleteTag}
                                onAdd={this.handleAddTag}
                                suggestions={this.props.allTags}
                            />
                        </div>
                    }

                    {
                        this.props.object.autocreated && !this.props.objectReadOnly && <WarningPanel
                            className={classes.warning}
                            text="Dieses Objekt wurde automatisch erzeugt. Manuelle Änderungen verfallen beim nächsten Software-Update unter Umständen."
                        />
                    }

                    <div className={classes.footer}>
                        <div className={classes.objectInfoArea}>
                            <Typography className={classes.objectInfoText} variant="body2">{this.formatTimestampAndUser("erzeugt", 'timestamp-created', 'user-created')}</Typography>
                            <Typography className={classes.objectInfoText} variant="body2">{this.formatTimestampAndUser("zuletzt geändert", 'timestamp-modified', 'user-modified')}</Typography>
                            <Typography className={classes.objectInfoText} variant="body2">{this.formatTimestampAndUser("gelöscht", 'timestamp-deleted', 'user-deleted')}</Typography>
                        </div>
                        <div className={classes.buttonArea}>
                            {this.props.isElementField ? null : <Button
                                className={classes.button}
                                variant="contained"
                                color={this.props.modified ? "primary" : "default"}
                                disabled={this.props.objectReadOnly}
                                onClick={() => this.props.onSaveObject(this.props.object, false)}
                            >
                                {"save"}
                            </Button>}
                            <Button
                                className={classes.button}
                                variant="contained"
                                color={this.props.modified ? "primary" : "default"}
                                disabled={this.props.objectReadOnly}
                                onClick={() => this.props.onSaveObject(this.props.object, true)}
                            >
                                {this.props.isElementField ? "OK" : "save & close"}
                            </Button>
                            <Button
                                className={classes.button}
                                variant="contained"
                                onClick={() => this.props.onCancel()}
                            >
                                cancel
                            </Button>
                        </div>
                    </div>
                </Typography>
            </div>
        );
    }

    renderFieldsOrTabs(tabIndex) {

        if (tabIndex === OBJECT_ACCESS_TAB_INDEX) {
            if (!this.props.changeObjectAccess) return null;
            return (
                <ObjectFieldSet
                    fieldDefinitions={this.props.accessDefinition.fieldsets[0].fields}
                    objectId={this.props.objectId}
                    objectFields={this.props.object.objectaccess}
                    readOnly={this.props.objectReadOnly}
                    scope={['objectaccess']}
                    onObjectValueChange={(name, value) => {
                        this.props.updateObjectValueAction(this.props.objectId, ['objectaccess', name], value);
                    }}
                />
            );
        }

        else if (tabIndex === OBJECT_REMARKS_TAB_INDEX) {
            const fieldDefs = [
                {
                    id: "remarks",
                    name: "Bemerkungen",
                    type: "text",
                    maxlength: 5000
                },
                {
                    id: "todo",
                    name: "zu erledigen",
                    type: "text",
                    maxlength: 5000
                }
            ];
            return (
                <div className={this.props.classes.fieldSetContainer}>
                    <div className={this.props.classes.fieldContainer}>
                        {
                            fieldDefs.map(
                                fieldDef => <ObjectField
                                    key={fieldDef.id}
                                    objectId={this.props.objectId}
                                    fieldDef={fieldDef}
                                    scope={[]}
                                    value={this.props.object[fieldDef.id]}
                                    readOnly={this.props.objectReadOnly}
                                    onObjectValueChange={(name, value) => {
                                        this.props.updateObjectValueAction(this.props.objectId, [name], value);
                                    }}
                                />
                            )
                        }
                    </div>
                </div>
            );
        }

        else if (tabIndex === OBJECT_REFERENCE_TAB_INDEX) {
            return <ObjectReferencePanel objectIds={this.props.referencedBy} />;
        }

        else if (tabIndex === OBJECT_HISTORY_TAB_INDEX) {
            return <ObjectHistoryPanel
                objectId={this.props.objectId}
                time={this.props.object["timestamp-modified"]}
                onUndo={object => this.handleUndoChange(object)}
            />;
        }

        else if (tabIndex === OBJECT_JSON_TAB_INDEX) {
            return <ObjectJsonPanel
                object={this.props.object}
                readOnly={this.props.objectReadOnly}
                onChange={value => this.props.updateWholeObjectAction(this.props.objectId, value)}
            />;
        }

        else {
            const selectedFieldSet = this.props.fieldSets[tabIndex];
            const fieldSetReadOnly = this.props.objectReadOnly || selectedFieldSet.accessLevel < ACCESS_WRITABLE;
            if (!selectedFieldSet) {
                return null;
            }
            else if (selectedFieldSet.fields) {
                const sideBarFieldSets = this.props.sideBarFieldSets.filter(
                    fieldSet => fieldSet.joinsidebar === selectedFieldSet.id
                );
                return (
                    <ObjectFieldSetPanel
                        fieldSet={selectedFieldSet}
                        sideBarFieldSets={sideBarFieldSets}
                        objectType={this.props.objectType}
                        objectId={this.props.objectId}
                        object={this.props.object}
                        readOnly={fieldSetReadOnly}
                        scope={['fields']}
                        onObjectValueChange={(name, value) => {
                            this.props.updateObjectValueAction(this.props.objectId, ['fields', name], value);
                        }}
                    />
                );
            }
            else if (selectedFieldSet.fieldsets) {
                return (
                    <ObjectTabSet
                        fieldSet={selectedFieldSet}
                        sideBarFieldSets={this.props.sideBarFieldSets}
                        objectType={this.props.objectType}
                        objectId={this.props.objectId}
                        object={this.props.object}
                        readOnly={fieldSetReadOnly}
                        scope={['fields']}
                        onObjectValueChange={(name, value) => {
                            this.props.updateObjectValueAction(this.props.objectId, ['fields', name], value);
                        }}
                        tabIndex={this.props.tabIndexes[tabIndex] || 0}
                        onTabChange={(event, index) => {this.props.changeTab(tabIndex, index)}}
                    />
                );
            }
            else {
                return null;
            }
        }
    }

    arePropsValid() {
        return this.props.fieldSets !== undefined;
    }

    formatTimestampAndUser(verb, timeField, userField) {
        if (!this.props.object[timeField]) return "";
        const timePart = toUserFriendlyDateTimeString(new Date(this.props.object[timeField]));
        if (!this.props.object[userField]) {
            return verb + " " + timePart;
        }
        const userPart = this.props.object[userField];
        return verb + " " + timePart + " von " + userPart;
    }

}

const styles = {
    title: {
        margin: "auto",
        textAlign: "center",
        overflow: 'hidden',
        whiteSpace: "nowrap",
        textOverflow: 'ellipsis',
        maxWidth: 350
    },
    objectTypeLabel: {
        textAlign: "center"
    },
    tabs: {
        boxShadow: "none"
    },
    footer: {
        display: "flex",
        justifyContent: "space-between"
    },
    objectInfoArea: {
        display: "inline-block",
        textAlign: "left",
        padding: "20px 5px 0px 80px"
    },
    objectInfoText: {
        color: "#aaa"
    },
    fieldsContainer: {
        margin: "0px 80px",
        overflow: "hidden"
    },
    fieldSetContainer: {
        width: "100%",
        display: "flex"
    },
    fieldContainer: {
        width: "100%",
    },
    buttonArea: {
        display: "inline-block",
        textAlign: "right",
        padding: "20px 80px 20px 5px"
    },
    button: {
        float: "right",
        margin: "0px 0px 20px 20px"
    },
    warning: {
        margin:"20px 80px 0px 75px"
    }
};

const mapStateToProps = (state, props) => {

    const isNewObject = !props.object["timestamp-modified"]; // due to a bug timestamp-created is not always present in objects created automatically
    const isOwnObject = isNewObject || (state.authorization.userInfo && props.object["user-created"] === state.authorization.userInfo.id)
    const privileges = state.authorization.userPrivileges;
    const typeDefinition = state.types.typeDefinitions[props.objectType];
    const objectInfo = state.editing.objectInfo[props.objectId] || {};
    const objectAccessLevel = objectInfo.accessLevel || ACCESS_UNDEFINED;
    const fieldSetAccessLevels = objectInfo.fieldsetAccessLevels || {};
    const visibleFieldSets = typeDefinition && typeDefinition.fields && typeDefinition.fields.definition ?
        typeDefinition.fields.definition.fieldsets.map(fieldSet => ({
            ...fieldSet,
            accessLevel: fieldSetAccessLevels[fieldSet.id] || ACCESS_WRITABLE
        }))
        .filter(
            fieldSet => fieldSet.accessLevel > ACCESS_HIDDEN
        ) : [];
    const fieldSets = visibleFieldSets.filter(
        fieldSet => !fieldSet.joinsidebar
    );
    const sideBarFieldSets = visibleFieldSets.filter(
        fieldSet => !!fieldSet.joinsidebar
    );

    return {
        accessDefinition: state.types.typeDefinitions["access"].fields.definition,
        fieldSets: fieldSets,
        sideBarFieldSets: sideBarFieldSets,
        tabIndexes: selectCurrentTabIndexes(state.editing),
        objectAccessLevel: objectAccessLevel,
        objectReadOnly: props.readOnly || objectAccessLevel < ACCESS_WRITABLE,
        referencedBy: objectInfo.referencedBy || [],
        allTags: state.editing.allTags,
        changeObjectAccess: props.isElementField ? null : (privileges.changeOtherObjectAccess || (privileges.changeOwnObjectAccess && isOwnObject)),
        accessObjectHistory: !props.isElementField && privileges.changeOwnObjectAccess,
        accessObjectJson: !props.isElementField && privileges.accessObjectJson,
        hasRemarkTab: !props.isElementField
    };
};

export default connect(mapStateToProps, {
    updateObjectValueAction, updateWholeObjectAction,
    deleteTag, addTag, changeTab, refreshObjectReferences
})(withStyles(styles)(ObjectEditPanel));